import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { FileText, Loader2, X } from "lucide-react";

const fmtSize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

function ImgThumb({ clientId, att, onOpen }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true, obj = null;
    api.get(`/clients/${clientId}/attachments/${att.id}`, { responseType: "blob" })
      .then((r) => { obj = URL.createObjectURL(r.data); if (alive) setUrl(obj); })
      .catch(() => {});
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [clientId, att.id]);
  if (!url) return <div className="w-24 h-20 rounded-[3px] border border-slate-200 bg-slate-50 flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin text-slate-300" /></div>;
  return (
    <button type="button" onClick={() => onOpen(url)} title={att.name} data-testid={`attachment-img-${att.id}`}
      className="block w-24 h-20 rounded-[3px] border border-slate-200 overflow-hidden cursor-zoom-in hover:border-[#B7955B] transition-colors">
      <img src={url} alt={att.name} className="w-full h-full object-cover" />
    </button>
  );
}

function PdfChip({ clientId, att }) {
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/clients/${clientId}/attachments/${att.id}`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank", "noopener");
    } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={open} disabled={busy} data-testid={`attachment-pdf-${att.id}`}
      className="inline-flex items-center gap-2 px-2.5 py-1.5 text-xs border border-slate-200 rounded-[3px] bg-slate-50 hover:border-[#B7955B] text-slate-700 max-w-[220px]">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin shrink-0" /> : <FileText className="w-3.5 h-3.5 text-rose-600 shrink-0" />}
      <span className="truncate">{att.name}</span>
      <span className="text-slate-400 shrink-0">{fmtSize(att.size || 0)}</span>
    </button>
  );
}

export function AttachmentList({ clientId, attachments }) {
  const [lightbox, setLightbox] = useState(null);
  if (!attachments?.length) return null;
  const imgs = attachments.filter((a) => (a.mime || "").startsWith("image/"));
  const pdfs = attachments.filter((a) => a.mime === "application/pdf");
  return (
    <div className="mt-2 space-y-1.5">
      {imgs.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {imgs.map((a) => <ImgThumb key={a.id} clientId={clientId} att={a} onOpen={setLightbox} />)}
        </div>
      )}
      {pdfs.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {pdfs.map((a) => <PdfChip key={a.id} clientId={clientId} att={a} />)}
        </div>
      )}
      {lightbox && (
        <div className="fixed inset-0 z-[80] bg-slate-900/85 flex items-center justify-center p-4 cursor-zoom-out" onClick={() => setLightbox(null)} data-testid="attachment-lightbox">
          <img src={lightbox} alt="Allegato" className="max-w-full max-h-[88vh] rounded-[3px] shadow-2xl" />
          <button className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-white/80 hover:text-white" aria-label="Chiudi"><X className="w-6 h-6" /></button>
        </div>
      )}
    </div>
  );
}

export function PendingAttachments({ items, onRemove }) {
  if (!items.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mb-2">
      {items.map((p, i) => (
        <div key={i} className="relative group/att" data-testid={`upd-pending-${i}`}>
          {p.preview ? (
            <img src={p.preview} alt={p.file.name} className="w-20 h-16 object-cover rounded-[3px] border border-slate-200" />
          ) : (
            <div className="w-36 h-16 px-2 rounded-[3px] border border-slate-200 bg-slate-50 flex flex-col items-center justify-center gap-0.5">
              <FileText className="w-4 h-4 text-rose-600" />
              <span className="text-[10px] text-slate-500 truncate w-full text-center">{p.file.name}</span>
            </div>
          )}
          <button type="button" onClick={() => onRemove(i)} title="Rimuovi"
            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-slate-800 text-white flex items-center justify-center opacity-90 hover:bg-rose-600">
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
    </div>
  );
}
